/**
 * Created by h3 on 2018-10-16.
 */

import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {Background, IBackgroundConfig} from "@nolimitcity/slot-game/bin/core/background/Background";
import {IGameModeChangedData} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ResourcesGroupName} from "@nolimitcity/slot-game/bin/core/resource/ResourcesGroupName";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {IResizeData} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Circ, Power4, TimelineLite, TimelineMax, TweenLite} from "gsap";
import {Fruits} from "../Fruits";
import {FruitsGameMode} from "../gamemode/FruitsGameMode";

export interface IFruitsStillImageBackgroundImagesConfig {
    readonly NORMAL:{
        landscape:{
            image:string;
            offset?:PIXI.Point;
        },
        portrait:{
            image:string;
            offset?:PIXI.Point;
        }
    };
    readonly BONUS:{
        landscape:{
            image:string;
            offset?:PIXI.Point;
        },
        portrait:{
            image:string;
            offset?:PIXI.Point;
        }
    };
}

export interface IFruitsStillImageBackgroundConfig extends IBackgroundConfig {
    readonly backgroundImages:IFruitsStillImageBackgroundImagesConfig;
    readonly stretch:boolean;
}

export class FruitsBasicBackground extends Background {

    private readonly _normalLandscapeTexture:string;
    private readonly _normalPortraitTexture:string;
    private readonly _bonusLandscapeTexture:string;
    private readonly _bonusPortraitTexture:string;

    protected _activeBackground:PIXI.Sprite;

    private _config:IFruitsStillImageBackgroundConfig;
    private _isPortrait:boolean;
    private _activeTexture:string;
    private _light:PIXI.spine.Spine;
    private _container:PIXI.Container;
    private _idleTl:TimelineMax;
    private _shakeTimeline:TimelineLite | null;
    private _screenWidth:number;
    private _screenHeight:number;
    private _leftOffset:number;
    private _topOffset:number;
    private _centerX:number;
    private _centerY:number;

    constructor(config:IFruitsStillImageBackgroundConfig, resourceGroup?:ResourcesGroupName) {
        super(config, resourceGroup);

        Fruits.basicBackground = this;

        this._config = config;

        this._normalLandscapeTexture = this._config.backgroundImages.NORMAL.landscape.image;
        this._normalPortraitTexture = this._config.backgroundImages.NORMAL.portrait.image;
        this._bonusLandscapeTexture = this._config.backgroundImages.BONUS.landscape.image;
        this._bonusPortraitTexture = this._config.backgroundImages.BONUS.portrait.image;
    }

    protected initAnimations():void {
        super.initAnimations();

        this._container = new PIXI.Container();
        this.addChild(this._container);

        this._light = new PIXI.spine.Spine(GameResources.getSpineAsset("spineBackgroundIdle"));
        this._light.position.set(650, 380);

        this._idleTl = new TimelineMax({repeat : -1, repeatDelay : 5});
        this._idleTl.add(new SpineTween(this._light, 0, "Light_A"));
    }

    protected onResize(resizeData:IResizeData):void {
        this._screenWidth = resizeData.width / this._layer.scale.x;
        this._screenHeight = resizeData.height / this._layer.scale.y;
        this._leftOffset = -this._layer.x / this._layer.scale.x;
        this._topOffset = -this._layer.y / this._layer.scale.y;
        this._centerX = this._screenWidth / 2 + this._leftOffset;
        this._centerY = this._screenHeight / 2 + this._topOffset;

        if(this._shakeTimeline && this._shakeTimeline.isActive()) {
            this._shakeTimeline.pause();
            this._shakeTimeline.kill();
            this._shakeTimeline.clear();
        }

        this._shakeTimeline = null;

        if(this._activeBackground) {
            const imgRatio = this._activeBackground.width / this._activeBackground.height;
            imgRatio.toFixed(2);

            if(this._screenWidth > this._activeBackground.width) {
                this._activeBackground.width = this._screenWidth;
                this._activeBackground.height = this._screenWidth / imgRatio;
            }


            this._activeBackground.position.set(this._centerX, this._centerY);
        }

        this._isPortrait = resizeData.orientation === Orientation.PORTRAIT;

        this.instantUpdate();
    }

    protected onChangeBackground(data:IGameModeChangedData):boolean {
        if(!super.onChangeBackground(data)) {
            return false;
        }

        this.animationUpdate();

        return true;
    }

    private evaluateBackgroundTexture():string {
        if((this._currentMode === FruitsGameMode.BONUS_GAME) && this._isPortrait) {
            return this._bonusPortraitTexture;
        }

        if((this._currentMode === FruitsGameMode.BONUS_GAME) && !this._isPortrait) {
            return this._bonusLandscapeTexture;
        }

        if((this._currentMode !== FruitsGameMode.BONUS_GAME) && this._isPortrait) {
            return this._normalPortraitTexture;
        } else {
            return this._normalLandscapeTexture;
        }
    }

    private animationUpdate():void {
        let newActiveTexture = this.evaluateBackgroundTexture();

        if(this._activeTexture === newActiveTexture) {
            return;
        }

        const tl:TimelineLite = new TimelineLite();
        if(this._activeBackground) {
            const newBackground = new PIXI.Sprite(GameResources.getTextures(newActiveTexture)[0]);
            newBackground.anchor.set(0.5, 0.5);
            newBackground.alpha = 0;
            newBackground.position.set(this._centerX, this._centerY);
            this.addChild(newBackground);
            this.updateIdleFx();

            tl.add([
                TweenLite.fromTo(this._activeBackground, 1, {alpha : 1}, {alpha : 0}),
                TweenLite.fromTo(newBackground, 1, {alpha : 0}, {alpha : 1}),
            ]);

            tl.add(() => {
                this.removeChild(this._activeBackground);
                this._activeBackground = newBackground;
                this._activeTexture = newActiveTexture;
            })
        } else {
            this.instantUpdate();
        }
    }

    private instantUpdate():void {
        let newActiveTexture = this.evaluateBackgroundTexture();

        if(this._activeTexture === newActiveTexture) {
            return;
        }

        const newBackground = new PIXI.Sprite(GameResources.getTextures(newActiveTexture)[0]);
        newBackground.anchor.set(0.5, 0.5);
        newBackground.position.set(this._centerX, this._centerY);
        this.addChild(newBackground);
        this.removeChild(this._activeBackground);
        this._activeBackground = newBackground;
        this._activeTexture = newActiveTexture;
        this.updateIdleFx();
    }

    private updateIdleFx() {
        if(!this._isPortrait) {
            this.removeChild(this._container);

            this._container = new PIXI.Container();
            this.addChild(this._container);
            this._container.addChild(this._light);

            this._idleTl.play();
            this._container.alpha = 1;
        } else {
            this._idleTl.pause();
            this._container.alpha = 0;
        }
    }

    public backgroundShake():void {
        this._shakeTimeline = new TimelineLite();

        this._shakeTimeline.add(TweenLite.to(this.scale, 0.04, {x : 1.04, y : 1.04, ease : Power4.easeOut}));
        this._shakeTimeline.add(TweenLite.to(this.scale, 0.08, {x : 0.96, y : 0.96, ease : Power4.easeOut}));
        this._shakeTimeline.add(TweenLite.to(this.scale, 0.08, {x : 1, y : 1, ease : Circ.easeOut}));
    }
}